    // Mark messages as read via REST API when chat is opened
   
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import ChatBar from "./chatbar";
import {
    ensureChatSocket,
    getChatSocket,
    trackPendingUpload,
    patchPendingUpload,
    clearPendingUpload,
    mergeMessagesWithPending,
    subscribeUploadEvents,
    shouldFailUploadsOnDisconnect,
} from "../../services/chatSocketManager";
import {
    mirrorUploadStarted,
    mirrorUploadProgress,
    mirrorUploadFinished,
} from "../../services/durableMediaUpload";
import { shareMessageToContacts } from "../../utils/shareChatMessage";
import { isAdminSupportContact } from "../../utils/supportContact";
import { revokeBlobUrl } from "../../utils/resolveMediaUrl";
import {
    logMediaUpload,
    MEDIA_UPLOAD_TIMEOUT_MS,
    shouldLogMediaUpload,
    summarizeSocketPayload,
} from "../../utils/mediaUploadLogger";
import {
    applyContactListPatch,
    CONTACT_SYNC_SOCKET_EVENTS,
    extractContactFromSocketPayload,
    isContactSyncEventName,
    mergeContactRecord,
} from "../../utils/contactListSync";
import FriendVoiceSendModal from "./models/voiceSendModal";
import ChatMessagesArea from "./chatMessageArea";
import GroupChatScreen from "../groupScreen/groupchatcomponent";
import { GroupDetails } from "../groupScreen/groupdetails";





// ---------- Main Chat Messages Area ----------


export default function ChatScreen() {

    const navigate = useNavigate();
    const { user_id: routeUserId } = useParams();

    const [contacts, setContacts] = useState([]);            // chat list (get_chat_list)
    const [contactDetails, setContactDetails] = useState([]); // full contact details (get-contacts)
    const [birthday, setBirthday] = useState([]);
    const [friendRes, setFriendRes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [chatListLoaded, setChatListLoaded] = useState(false);

    const [selectedContactId, setSelectedContactId] = useState(null);
    const [selectedGroupId, setSelectedGroupId] = useState(null);  // 🔹 NEW: Track selected group
    const [showGroupDetails, setShowGroupDetails] = useState(false);  // 🔹 NEW: Track group details view
    const [groupList, setGroupList] = useState([]);               // 🔹 NEW: Groups list
    const token = Cookies.get("auth_token");
    const [autoSelectPending, setAutoSelectPending] = useState(null);
    const [showSidebar, setShowSidebar] = useState(true);

    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState("");

    const [showAttachModal, setShowAttachModal] = useState(false);
    const [showVoiceAttachModal, setShowVoiceAttachModal] = useState(false);
    const [showVoice, setShowVoice] = useState(false); // Show voice recorder UI by default
    const [voiceTime, setVoiceTime] = useState("0:13");
    const [showVoiceSendModal, setShowVoiceSendModal] = useState(false);
    const [showPermissionModal, setShowPermissionModal] = useState(false);
    const [profile, setProfile] = useState(null);
    const [status, setStatus] = useState("");
    // File upload progress tracking
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);

    // Remove an optimistic uploading message by tempId
    const handleCancelUpload = useCallback((tempId, contactId = selectedContactIdRef.current) => {
        clearActiveUpload(tempId);
        mirrorUploadFinished(tempId);
        if (contactId) clearPendingUpload(contactId, tempId);
        setMessages((prev) => {
            const removed = prev.find((m) => m.tempId === tempId);
            if (removed?.fileUrl?.startsWith("blob:")) {
                URL.revokeObjectURL(removed.fileUrl);
            }
            return prev.filter((m) => m.tempId !== tempId);
        });
        setIsUploading(false);
        setUploadProgress(0);
    }, []);

    const socketRef = useRef(null);
    const selectedContactIdRef = useRef(null);
    const selectedGroupIdRef = useRef(null);
    const markedReadRef = useRef({});
    const activeUploadsRef = useRef(new Map()); // tempId -> { file, timeoutId }
    const recentUploadKeysRef = useRef(new Map()); // `${contactId}:${name}:${size}` -> timestamp
    const fileInputRef = useRef(null);
    const contactsRef = useRef([]);
    const contactDetailsRef = useRef([]);

    // ----------------------------
    // CONSTANTS
    // ----------------------------
    const DEDUP_WINDOW_TEXT = 30000;     // 30 seconds for text message deduplication
    const DEDUP_WINDOW_FILE = 10000;     // 10 seconds for file message deduplication
    const SOCKET_URL = "https://phonebookplus.app";
    const SOCKET_PATH = "/api/socket.io/";
    const SOCKET_TIMEOUT = 20000;
    const SOCKET_RECONNECT_DELAY = 1000;
    const SOCKET_RECONNECT_ATTEMPTS = 5;
    const MARK_READ_DELAY = 500;         // Delay before marking messages as read
    const AUTO_MARK_READ_DELAY = 300;    // Delay after adding message before marking as read
    const SERVER_PAYLOAD_TOO_LARGE_MSG =
        "Upload failed — the server rejected the file (HTTP 413 Content Too Large). Contact support if this persists.";
    const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const ALLOWED_AUDIO_TYPES = ['audio/mpeg', 'audio/wav', 'audio/webm', 'audio/ogg'];
    const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];

    const resolveMessageMediaType = ({ type, messageType, mime, fileName, fileUrl }) => {
        const rawType = String(type || messageType || "text").toLowerCase();
        if (rawType === "image" || rawType === "audio" || rawType === "video") return rawType;

        const mimeValue = String(mime || "").toLowerCase();
        if (mimeValue.startsWith("image/")) return "image";
        if (mimeValue.startsWith("audio/")) return "audio";
        if (mimeValue.startsWith("video/")) return "video";

        const fileHint = `${fileUrl || ""} ${fileName || ""}`.toLowerCase();
        if (/\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(fileHint)) return "image";
        if (/\.(mp3|wav|ogg|m4a|aac|flac)(\?.*)?$/i.test(fileHint)) return "audio";
        if (/\.(mp4|webm|mov|avi|mkv|m4v)(\?.*)?$/i.test(fileHint)) return "video";

        if (!fileName && !fileUrl) return rawType === "file" ? "text" : rawType;
        if (!rawType || rawType === "text" || rawType === "file") return "document";
        return rawType;
    };

    const MEDIA_TYPE_RANK = { text: 0, file: 0, document: 1, audio: 2, image: 3, video: 3 };

    const pickPreferredMediaType = (currentType, nextType) => {
        const currentRank = MEDIA_TYPE_RANK[String(currentType || "").toLowerCase()] ?? 1;
        const nextRank = MEDIA_TYPE_RANK[String(nextType || "").toLowerCase()] ?? 1;
        return nextRank > currentRank ? nextType : currentType;
    };

    const isMessageForActiveChat = (data = {}) => {
        const activeId = String(selectedContactIdRef.current || "");
        if (!activeId) return false;

        const currentUserId = String(profile?.user_id || "");
        const { senderId, receiverId } = getMessageIdentity(data);
        const sender = senderId ? String(senderId) : "";
        const receiver = receiverId ? String(receiverId) : "";

        if (sender && receiver) {
            return (sender === activeId && receiver === currentUserId)
                || (sender === currentUserId && receiver === activeId);
        }
        if (sender) return sender === activeId || sender === currentUserId;
        if (receiver) return receiver === activeId || receiver === currentUserId;
        return true;
    };

    const mergeSocketMessageUpdate = (existing, {
        messageText,
        type,
        fileName,
        fileUrl,
        mime,
        fileSize,
        thumbnailImage,
        shareType,
        viewedBy,
        fileDeleted,
        data,
    }) => {
        const resolvedUrl = fileUrl ? resolveServerFileUrl(fileUrl) : null;

        if (existing.fileUrl?.startsWith("blob:") && resolvedUrl) {
            revokeBlobUrl(existing.fileUrl);
        }

        return {
            ...existing,
            text: messageText || existing.text,
            type: pickPreferredMediaType(existing.type, type),
            fileName: fileName || existing.fileName,
            fileUrl: resolvedUrl || existing.fileUrl,
            fileUrlRaw: fileUrl || existing.fileUrlRaw,
            mime: mime || existing.mime,
            fileSize: fileSize || existing.fileSize,
            thumbnail_image: thumbnailImage || existing.thumbnail_image,
            shareType: shareType || existing.shareType,
            viewedBy: viewedBy?.length ? viewedBy : existing.viewedBy,
            fileDeleted: fileDeleted ?? existing.fileDeleted,
            uploading: false,
            uploadFailed: false,
            uploadError: undefined,
            tempId: undefined,
            _clientUploadId: undefined,
            original: data || existing.original,
            created_at: data?.created_at || data?.timestamp || existing.created_at || existing.original?.created_at,
            time: data?.custom_time || data?.time || existing.time,
        };
    };

    const resolveFileType = (file, fileTypeHint) => {
        if (fileTypeHint === "photo" || fileTypeHint === "camera") {
            if (file?.type?.startsWith("video")) return "video";
            const ext = file?.name?.split(".")?.pop()?.toLowerCase() || "";
            const videoExts = ["mp4", "webm", "ogg", "mov", "avi", "mkv"];
            if (videoExts.includes(ext)) return "video";
            return "image";
        }
        if (fileTypeHint === "video") return "video";
        if (fileTypeHint === "document") return "document";
        if (file?.type?.startsWith("image")) return "image";
        if (file?.type?.startsWith("audio")) return "audio";
        if (file?.type?.startsWith("video")) return "video";
        const ext = file?.name?.split(".")?.pop()?.toLowerCase() || "";
        const imageExts = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];
        const audioExts = ["mp3", "wav", "ogg", "aac", "m4a", "flac"];
        const videoExts = ["mp4", "webm", "ogg", "mov", "avi", "mkv"];
        if (imageExts.includes(ext)) return "image";
        if (audioExts.includes(ext)) return "audio";
        if (videoExts.includes(ext)) return "video";
        return "document";
    };

    const getFileDedupWindowMs = (fileSizeBytes = 0) => {
        const baseMs = DEDUP_WINDOW_FILE * 2;
        const sizeMb = fileSizeBytes / (1024 * 1024);
        return Math.min(Math.max(baseMs, sizeMb * 5000), 10 * 60 * 1000);
    };

    const clearActiveUpload = (tempId) => {
        activeUploadsRef.current.delete(tempId);
    };

    const markOptimisticUploadFailed = (contactId, tempId, errorMsg) => {
        clearActiveUpload(tempId);
        mirrorUploadFinished(tempId, { failed: true, error: errorMsg || "Upload failed" });
        if (contactId) {
            patchPendingUpload(contactId, tempId, {
                uploading: false,
                uploadFailed: true,
                uploadError: errorMsg || "Upload failed",
            });
        }
        setMessages((prev) =>
            prev.map((m) =>
                m.id === tempId || m.tempId === tempId
                    ? { ...m, uploading: false, uploadFailed: true, uploadError: errorMsg || "Upload failed" }
                    : m
            )
        );
    };

    const registerActiveUpload = (contactId, tempId, file, optimisticMessage, fileType) => {
        clearActiveUpload(tempId);
        const startedAt = Date.now();
        const timeoutId = setTimeout(() => {
            if (!activeUploadsRef.current.has(tempId)) return;
            const socket = getChatSocket();
            if (shouldLogMediaUpload(fileType, file)) {
                logMediaUpload("1-on-1", "emit-timeout", file, {
                    tempId,
                    fileType,
                    contactId,
                    timeoutMs: MEDIA_UPLOAD_TIMEOUT_MS,
                    elapsedMs: Date.now() - startedAt,
                    socketConnected: Boolean(socket?.connected),
                    socketId: socket?.id ?? null,
                });
            }
            markOptimisticUploadFailed(
                contactId,
                tempId,
                "Upload timed out — the server did not respond. Please try again."
            );
            toast.error("Upload timed out. Please try again.");
        }, MEDIA_UPLOAD_TIMEOUT_MS);
        activeUploadsRef.current.set(tempId, { file, contactId, startedAt, fileType });
        if (shouldLogMediaUpload(fileType, file)) {
            logMediaUpload("1-on-1", "registered-timeout", file, {
                tempId,
                fileType,
                contactId,
                timeoutMs: MEDIA_UPLOAD_TIMEOUT_MS,
                startedAt: new Date(startedAt).toISOString(),
            });
        }
        if (contactId && optimisticMessage) {
            const { onCancelUpload, ...stored } = optimisticMessage;
            trackPendingUpload(contactId, tempId, stored, timeoutId, { notify: false });
        }
    };

    const failActiveUploadsOnDisconnect = (reason) => {
        if (!shouldFailUploadsOnDisconnect(reason)) {
            return;
        }
        const entries = [...activeUploadsRef.current.entries()];
        if (entries.length === 0) return;
        entries.forEach(([tempId, { file, contactId, fileType, startedAt }]) => {
            if (shouldLogMediaUpload(fileType, file)) {
                logMediaUpload("1-on-1", "socket-disconnect-during-upload", file, {
                    tempId,
                    fileType,
                    contactId,
                    reason,
                    elapsedMs: startedAt ? Date.now() - startedAt : null,
                });
            }
            markOptimisticUploadFailed(contactId, tempId, SERVER_PAYLOAD_TOO_LARGE_MSG);
        });
        toast.error("Upload failed — connection lost while sending the file.");
    };

    const attachCancelHandler = useCallback((message) => {
        if (!message?.tempId || !message.uploading) return message;
        return {
            ...message,
            onCancelUpload: () => handleCancelUpload(message.tempId, selectedContactIdRef.current),
        };
    }, [handleCancelUpload]);

    const isEncryptedPayloadObject = (value) => {
        if (!value || typeof value !== "object" || Array.isArray(value)) return false;
        return (
            Object.prototype.hasOwnProperty.call(value, "ciphertext") &&
            Object.prototype.hasOwnProperty.call(value, "nonce")
        );
    };

    // If a group is selected but not found in `contacts` or `groupList`, fetch groups once
    useEffect(() => {
        if (!selectedGroupId) return;
        const existsInContacts = contacts.some((g) => String(g.group_id) === String(selectedGroupId));
        const existsInList = groupList.some((g) => String(g.group_id) === String(selectedGroupId));
        if (!existsInContacts && !existsInList) {
            fetchGroups().catch(() => {});
        }
    }, [selectedGroupId, contacts, groupList]);

    const toDisplayText = (value, fallback = "") => {
        if (value === null || value === undefined) return fallback;
        if (typeof value === "string") return value;
        if (typeof value === "number" || typeof value === "boolean") return String(value);
        if (isEncryptedPayloadObject(value)) return "[Encrypted message]";
        try {
            return JSON.stringify(value);
        } catch {
            return fallback;
        }
    };

    // ----------------------------
    // Helper for GET API calls
    // ----------------------------
    const apiGet = async (endpoint, token) => {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`, {
            headers: { Authorization: `Bearer ${token}` }
        });

        const data = await res.json();
        if (!res.ok)
            throw new Error(data?.message || `Failed to fetch ${endpoint}`);

        if (endpoint === "/birthdays/getall") {
            return data || {};
        }

        return data?.data?.[0] || {};
    };

    const apiPost = async (endpoint, body, token) => {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/x-www-form-urlencoded"
            },
            body: body ? new URLSearchParams(body).toString() : undefined,
        });

        const data = await res.json();
        if (!res.ok)
            throw new Error(data?.message || `Failed to post ${endpoint}`);

        // Prefer arrays when returning contact lists
        if (Array.isArray(data?.data)) return data.data;
        if (Array.isArray(data?.contacts)) return data.contacts;
        return data?.data || data || [];
    };

    // Fetch groups (used when a group is selected but not present in `contacts`)
    const fetchGroups = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/group/list?include_all=1`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            const data = await res.json();
            const groups = data.groups || [];
            setGroupList(groups);
            return groups;
        } catch (err) {
            return [];
        }
    };


    // ----------------------------
    // MESSAGE DEDUPLICATION HELPERS
    // ----------------------------
    /**
     * Extract base filename by removing hash suffixes
     * Example: "file_abc123.png" -> "file.png"
     */
    const markMessagesRead = async (friend) => {
        if (!friend || !friend.user_id) return;
        // Guard: only call once per contact selection unless cleared
        if (markedReadRef.current[String(friend.user_id)]) {
            // already marked (or in-flight)
            return;
        }

        markedReadRef.current[String(friend.user_id)] = 'pending';

        try {
            const token = Cookies.get("auth_token");
            if (!token) {
                markedReadRef.current[String(friend.user_id)] = false;
                return;
            }
            const isAdminContact = /^admin/i.test(String(friend?.user_id || ""));
            const params = new URLSearchParams();
            params.append("user_id", friend.user_id);
            if (!isAdminContact && friend.phone_number) {
                // Use full phone number (with +) if available
                const receiverPhone = String(friend.phone_number).startsWith("+")
                    ? String(friend.phone_number)
                    : `+91${friend.phone_number}`;
                params.append("receiver_phone", receiverPhone);
            }
            const res = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/chat/messages/mark_read`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/x-www-form-urlencoded"
                    },
                    body: params.toString()
                }
            );
            if (res.ok) {
                // mark success and clear unread count in UI
                markedReadRef.current[String(friend.user_id)] = true;
                setContacts(prev => prev.map(c =>
                    String(c.user_id) === String(friend.user_id)
                        ? { ...c, unread_count: 0 }
                        : c
                ));
            } else {
                // API failed; allow retry
                markedReadRef.current[String(friend.user_id)] = false;
            }
        } catch (err) {
            markedReadRef.current[String(friend.user_id)] = false;
        }
    };
    useEffect(() => {
        contactsRef.current = contacts;
    }, [contacts]);

    useEffect(() => {
        contactDetailsRef.current = contactDetails;
    }, [contactDetails]);
    const getBaseFileName = (filename) => {
        if (!filename) return '';
        // Remove hash patterns that servers typically add
        let name = filename
            .replace(/_[a-f0-9]{32,}\./gi, '.')          // _hash.
            .replace(/-[a-f0-9]{32,}\./gi, '.')          // -hash.
            .replace(/_[a-f0-9]+\./gi, '.')              // _anything.
            .replace(/-[a-f0-9]+\./gi, '.')              // -anything.
            .toLowerCase()
            .trim();
        // Also remove any trailing hash after filename
        name = name.replace(/_[a-f0-9]{8,}$/gi, '');     // Remove _hash at end
        name = name.replace(/-[a-f0-9]{8,}$/gi, '');     // Remove -hash at end
        return name;
    };

    /**
     * Find optimistic text message matching server response
     * Returns the matching message object if found
     */
    const findMatchingOptimisticTextMessage = (messages, text) => {
        return messages.find(m =>
            m.isUser &&
            m.tempId &&
            m.text?.trim() === text?.trim() &&
            m.type === "text"
        );
    };

    /**
     * Find optimistic file message matching server response
     * Compares by base filename to handle server hash additions
     * Uses multiple matching strategies with priority levels
     */
    const findMatchingOptimisticFileMessage = (messages, fileName, text, fileUrl) => {
        if (!fileName && !text && !fileUrl) return null;

        const serverBaseFileName = getBaseFileName(fileName);
        const serverBaseText = getBaseFileName(text);
        const serverFileUrl = fileUrl ? String(fileUrl).trim().toLowerCase() : "";

        // Prefer the most recent optimistic upload (align with group chat)
        for (let i = messages.length - 1; i >= 0; i--) {
            const m = messages[i];
            if (!isLocalUploadMessage(m)) continue;

            let matched = false;

            // Priority 1: Match by filename (most reliable)
            if (m.fileName && serverBaseFileName) {
                const msgBaseFileName = getBaseFileName(m.fileName);
                if (msgBaseFileName && msgBaseFileName === serverBaseFileName) {
                    matched = true;
                }
            }

            // Priority 2: Match by normalized filename (handles server hash renames)
            if (!matched && m._normalizedFileName && fileName) {
                const normServer = String(fileName || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
                if (normServer && m._normalizedFileName === normServer) {
                    matched = true;
                }
            }

            // Priority 3: Match by text/caption (if filename is empty)
            if (!matched && serverBaseText && m.text) {
                const msgBaseText = getBaseFileName(m.text);
                if (msgBaseText && msgBaseText === serverBaseText) {
                    matched = true;
                }
            }

            // Priority 4: Match by URL/path when server emits file metadata instead of name
            if (!matched && serverFileUrl) {
                const msgFileUrl = String(m.fileUrl || m.fileUrlRaw || m.original?.file_url || m.original?.file_path || "").trim().toLowerCase();
                if (msgFileUrl && (msgFileUrl === serverFileUrl || msgFileUrl.endsWith(serverFileUrl.split("/").pop()))) {
                    matched = true;
                }
            }

            // Priority 5: Exact filename match (without hashing)
            if (!matched && fileName && m.fileName) {
                const cleanServerName = fileName.split('/').pop()?.toLowerCase()?.trim() || '';
                const cleanMsgName = m.fileName.split('/').pop()?.toLowerCase()?.trim() || '';
                if (cleanServerName && cleanServerName === cleanMsgName) {
                    matched = true;
                }
            }

            if (matched) return m;
        }

        return null;
    };

    /**
     * Get message age in milliseconds from its ID
     */
    const getMessageAge = (messageId) => {
        let timestamp = 0;
        if (typeof messageId === 'string' && messageId.includes('_')) {
            timestamp = parseInt(messageId.split('_')[1]) || 0;
        } else if (typeof messageId === 'number') {
            timestamp = messageId;
        }
        return timestamp ? Date.now() - timestamp : 0;
    };

    /**
     * Check if message is within deduplication window
     */
    const isWithinDeducationWindow = (messageId, windowMs) => {
        return getMessageAge(messageId) < windowMs;
    };

    const isWithinUploadWindow = (message, windowMs) => {
        if (message?.uploadStartedAt) {
            return Date.now() - message.uploadStartedAt < windowMs;
        }
        return isWithinDeducationWindow(message?.id, windowMs);
    };

    const isLocalUploadMessage = (message) => {
        if (!message?.isUser) return false;
        if (message.uploading || message.tempId || message._clientUploadId) return true;
        if (String(message.id || "").startsWith("temp_")) return true;
        // Only treat messages with an active upload timestamp as local (align with group chat).
        // Do not use message id age — server numeric ids were incorrectly treated as in-flight uploads.
        if (message?.uploadStartedAt) {
            return Date.now() - message.uploadStartedAt < getFileDedupWindowMs(message?._rawFileSize || 0);
        }
        return false;
    };

    const resolveServerFileUrl = (rawUrl) => {
        if (!rawUrl) return null;
        const value = String(rawUrl);
        if (value.startsWith("blob:")) return value;
        if (value.startsWith("http")) return value;
        return `${process.env.REACT_APP_API_BASE_URL}${value.startsWith("/") ? value : `/${value}`}`;
    };

    const messagesMatchById = (left, right) =>
        left != null && right != null && String(left) === String(right);

    /**
     * Replace optimistic message with server response
     */
    const replaceOptimisticMessage = (messages, oldMessage, newMessageId, serverData) => {
        const serverFile = serverData?.file || serverData?.file_url || serverData?.file_path || serverData?.url || null;
        const resolvedServerUrl = serverFile ? resolveServerFileUrl(serverFile) : null;

        return messages.map(m => {
            const isMatch =
                m.id === oldMessage.id ||
                m.tempId === oldMessage.tempId ||
                m.tempId === oldMessage.id ||
                messagesMatchById(m._clientUploadId, oldMessage._clientUploadId) ||
                messagesMatchById(m._clientUploadId, oldMessage.tempId) ||
                messagesMatchById(m._clientUploadId, oldMessage.id);

            if (!isMatch) return m;

            if (m.fileUrl?.startsWith("blob:") && resolvedServerUrl) {
                try { URL.revokeObjectURL(m.fileUrl); } catch (_) { /* ignore */ }
            }

            return {
                ...m,
                id: newMessageId,
                tempId: undefined,
                _clientUploadId: undefined,
                uploading: false,
                uploadFailed: false,
                uploadError: undefined,
                created_at: serverData?.created_at || serverData?.timestamp || m.created_at || m.original?.created_at,
                time: serverData?.custom_time || serverData?.time || m.time,
                original: serverData,
                ...(serverFile ? {
                    fileUrl: resolvedServerUrl || m.fileUrl,
                    fileUrlRaw: serverFile,
                    fileName: serverData?.filename || serverData?.file_name || m.fileName,
                    fileSize: serverData?.file_size_str || serverData?.file_size || m.fileSize,
                    mime: serverData?.mime_type || serverData?.mime || m.mime,
                    thumbnail_image: serverData?.thumbnail_image || serverData?.thumbnail_path || m.thumbnail_image,
                    shareType: serverData?.share_type || m.shareType,
                    viewedBy: serverData?.viewed_by || m.viewedBy,
                    fileDeleted: serverData?.file_deleted || m.fileDeleted,
                } : {}),
            };
        });
    };

    const collapseDuplicateOutgoingUploads = (messages, {
        uploadKey,
        serverMessageId,
    }) => {
        if (!uploadKey && !serverMessageId) return messages;

        const belongsToUploadGroup = (m) => {
            if (!m?.isUser) return false;
            if (uploadKey && (
                messagesMatchById(m._clientUploadId, uploadKey) ||
                messagesMatchById(m.tempId, uploadKey) ||
                messagesMatchById(m.id, uploadKey)
            )) {
                return true;
            }
            if (serverMessageId && messagesMatchById(m.id, serverMessageId)) {
                return true;
            }
            return false;
        };

        const group = messages.filter(belongsToUploadGroup);
        if (group.length <= 1) return messages;

        const scoreMessage = (m) => {
            let score = 0;
            if (serverMessageId && messagesMatchById(m.id, serverMessageId)) score += 100;
            if (m.fileUrl?.startsWith("http")) score += 50;
            if (m.fileUrl && !m.fileUrl.startsWith("blob:")) score += 30;
            if (!m.uploading) score += 10;
            if (m.original?.message_id || m.original?.id) score += 5;
            return score;
        };

        const best = group.reduce((a, b) => (scoreMessage(a) >= scoreMessage(b) ? a : b));

        return messages.filter((m) => {
            if (!belongsToUploadGroup(m)) return true;
            if (m === best) return true;
            revokeBlobUrl(m.fileUrl);
            return false;
        });
    };

    const mergeOutgoingFileEcho = (messages, {
        messageId,
        messageText,
        type,
        fileName,
        fileUrl,
        mime,
        fileSize,
        thumbnailImage,
        shareType,
        viewedBy,
        fileDeleted,
        data,
    }) => {
        const resolvedUrl = fileUrl ? resolveServerFileUrl(fileUrl) : null;
        let matched = findMatchingOptimisticFileMessage(messages, fileName, messageText, fileUrl);

        if (!matched && !fileName && messageText) {
            matched = findMatchingOptimisticTextMessage(messages, messageText);
        }

        if (!matched) {
            const hasRecentSameFile = messages.some((m) =>
                m.isUser &&
                fileName &&
                m.fileName &&
                getBaseFileName(m.fileName) === getBaseFileName(fileName) &&
                isLocalUploadMessage(m)
            );
            if (hasRecentSameFile) {
                return messages;
            }
            return null;
        }

        const uploadKey = matched._clientUploadId || matched.tempId || matched.id;

        if (uploadKey && selectedContactIdRef.current) {
            clearPendingUpload(selectedContactIdRef.current, uploadKey, { notify: false });
        }

        if (matched.fileUrl?.startsWith("blob:") && resolvedUrl) {
            revokeBlobUrl(matched.fileUrl);
        }

        const merged = {
            id: messageId,
            text: messageText || fileName || matched.text,
            type: pickPreferredMediaType(matched.type, type),
            isUser: true,
            created_at: data?.created_at || data?.timestamp || matched.created_at || matched.original?.created_at,
            time: data?.custom_time || data?.time || matched.time,
            fileName: fileName || matched.fileName,
            fileUrl: resolvedUrl || matched.fileUrl,
            fileUrlRaw: fileUrl || matched.fileUrlRaw,
            mime: mime || matched.mime,
            fileSize: fileSize || matched.fileSize,
            thumbnail_image: thumbnailImage || matched.thumbnail_image,
            shareType: matched.shareType || shareType,
            viewedBy: viewedBy?.length ? viewedBy : matched.viewedBy,
            fileDeleted: fileDeleted ?? matched.fileDeleted,
            uploading: false,
            uploadFailed: false,
            uploadError: undefined,
            tempId: undefined,
            _clientUploadId: undefined,
            original: data,
        };

        const next = messages.flatMap((m) => {
            const sameUpload =
                m === matched ||
                (uploadKey && (
                    messagesMatchById(m._clientUploadId, uploadKey) ||
                    messagesMatchById(m.tempId, uploadKey) ||
                    messagesMatchById(m.id, uploadKey)
                ));

            if (sameUpload) {
                if (m !== matched && m.fileUrl?.startsWith("blob:")) {
                    revokeBlobUrl(m.fileUrl);
                }
                return m === matched ? [merged] : [];
            }

            const duplicateSameFile =
                m.isUser &&
                fileName &&
                m.fileName &&
                getBaseFileName(m.fileName) === getBaseFileName(fileName) &&
                (
                    isLocalUploadMessage(m) ||
                    messagesMatchById(m.id, messageId) ||
                    (matched?.uploadStartedAt && getMessageTimestamp(m) >= matched.uploadStartedAt - 15000)
                );

            if (duplicateSameFile) {
                revokeBlobUrl(m.fileUrl);
                return [];
            }

            return [m];
        });

        return collapseDuplicateOutgoingUploads(next, { uploadKey, serverMessageId: messageId });
    };

    const mergeOutgoingFileEchoOrSkip = (messages, echoPayload) => {
        const merged = mergeOutgoingFileEcho(messages, echoPayload);
        if (merged) return merged;

        const { messageId, messageText, type, fileName, fileUrl, mime, fileSize, thumbnailImage, shareType, viewedBy, fileDeleted, data } = echoPayload;
        const resolvedUrl = fileUrl ? resolveServerFileUrl(fileUrl) : null;
        const fallbackIdx = [...messages].reverse().findIndex((m) =>
            m.isUser &&
            m.type !== "text" &&
            (isLocalUploadMessage(m) || String(m.id || "").startsWith("temp_"))
        );

        if (fallbackIdx === -1) {
            return messages;
        }

        const idx = messages.length - 1 - fallbackIdx;
        const matched = messages[idx];
        const uploadKey = matched._clientUploadId || matched.tempId || matched.id;

        if (matched.fileUrl?.startsWith("blob:") && resolvedUrl) {
            revokeBlobUrl(matched.fileUrl);
        }

        const patched = {
            id: messageId,
            text: messageText || fileName || matched.text,
            type: pickPreferredMediaType(matched.type, type),
            isUser: true,
            created_at: data?.created_at || data?.timestamp || matched.created_at || matched.original?.created_at,
            time: data?.custom_time || data?.time || matched.time,
            fileName: fileName || matched.fileName,
            fileUrl: resolvedUrl || matched.fileUrl,
            fileUrlRaw: fileUrl || matched.fileUrlRaw,
            mime: mime || matched.mime,
            fileSize: fileSize || matched.fileSize,
            thumbnail_image: thumbnailImage || matched.thumbnail_image,
            shareType: matched.shareType || shareType,
            viewedBy: viewedBy?.length ? viewedBy : matched.viewedBy,
            fileDeleted: fileDeleted ?? matched.fileDeleted,
            uploading: false,
            uploadFailed: false,
            uploadError: undefined,
            tempId: undefined,
            _clientUploadId: undefined,
            original: data,
        };

        const next = messages.map((m, i) => (i === idx ? patched : m));
        return collapseDuplicateOutgoingUploads(next, { uploadKey, serverMessageId: messageId });
    };

    const getMessageIdentity = (data = {}) => ({
        direction: String(data?.direction || data?.msg_direction || "").toLowerCase(),
        senderId: data?.sender_id || data?.from_user_id || data?.from_id || "",
        receiverId: data?.receiver_id || data?.to_user_id || data?.to_id || "",
        userId: data?.user_id || data?.userId || "",
    });

    const isCurrentUserMessage = (data = {}) => {
        const { direction, senderId, receiverId, userId } = getMessageIdentity(data);
        const currentUserId = String(profile?.user_id || "");
        const selectedId = String(selectedContactIdRef.current || selectedContactId || "");

        if (direction.includes("admin_to_user")) return false;
        if (direction.includes("user_to_admin")) return true;

        if (senderId) return String(senderId) === currentUserId;

        // Some image/file payloads only include user_id; treat it as sender if explicit sender fields are missing.
        if (userId) {
            if (String(userId) === currentUserId) return true;
            if (selectedId && String(userId) === selectedId) return false;
        }

        if (receiverId) {
            if (String(receiverId) === currentUserId) return false;
            if (selectedId && String(receiverId) === selectedId) return true;
        }

        return false;
    };

    const getMessageTimestamp = (message = {}) => {
        const raw =
            message?.created_at ||
            message?.timestamp ||
            message?.custom_time ||
            message?.time ||
            message?.original?.created_at ||
            message?.original?.timestamp ||
            message?.original?.custom_time ||
            message?.original?.time ||
            null;
        const time = raw ? new Date(raw).getTime() : 0;
        return Number.isFinite(time) ? time : 0;
    };

    const upsertItemById = (items, item, idKey = "user_id") => {
        const itemId = item?.[idKey];
        if (!itemId) return items;

        const index = items.findIndex((entry) => String(entry?.[idKey]) === String(itemId));
        if (index >= 0) {
            const next = [...items];
            next[index] = { ...next[index], ...item };
            return next;
        }

        return [...items, item];
    };

    const moveContactToTop = useCallback((userId, messageTime) => {
        setContacts((prev) => {
            const index = prev.findIndex(c => String(c.user_id) === String(userId));
            if (index === -1) return prev;

            const updatedContact = {
                ...prev[index],
                last_message_time: messageTime,
                unread_count:
                    String(userId) !== String(selectedContactIdRef.current)
                        ? (prev[index].unread_count || 0) + 1
                        : prev[index].unread_count || 0,
            };

            return [
                updatedContact,
                ...prev.filter((_, i) => i !== index)
            ];
        });
    }, []);

    const upsertGroupInChatList = useCallback((group, { markUnread } = {}) => {
        const groupId = group?.group_id;
        if (!groupId) return;

        const isActive = String(selectedGroupIdRef.current) === String(groupId);
        const readStatus = markUnread === undefined
            ? (isActive ? 0 : 1)
            : (markUnread && !isActive ? 1 : 0);

        const lastMessageTime =
            group.last_message_time ||
            group.updated_at ||
            group.created_at ||
            new Date().toISOString();

        const applyUpdate = (prev) => {
            const idx = prev.findIndex((entry) => String(entry.group_id) === String(groupId));
            const updated = idx >= 0
                ? {
                    ...prev[idx],
                    ...group,
                    last_message_time: lastMessageTime,
                    group_message_read: readStatus,
                }
                : {
                    ...group,
                    last_message_time: lastMessageTime,
                    group_message_read: readStatus,
                };

            if (idx >= 0) {
                return [updated, ...prev.filter((_, i) => i !== idx)];
            }
            return [updated, ...prev];
        };

        setContacts(applyUpdate);
        setGroupList(applyUpdate);
    }, []);

    const handleGroupMessageReceived = useCallback((groupId) => {
        const messageTime = new Date().toISOString();
        const isActive = String(selectedGroupIdRef.current) === String(groupId);

        const bumpGroup = (prev) => {
            const idx = prev.findIndex((entry) => String(entry.group_id) === String(groupId));
            if (idx < 0) return prev;

            const updated = {
                ...prev[idx],
                last_message_time: messageTime,
                group_message_read: isActive ? 0 : 1,
            };

            return [updated, ...prev.filter((_, i) => i !== idx)];
        };

        setContacts(bumpGroup);
        setGroupList(bumpGroup);
    }, []);

const markContactRead = useCallback((userId) => {
  setContacts(prev =>
    prev.map(c =>
      String(c.user_id) === String(userId)
        ? { ...c, unread_count: 0 }
        : c
    )
  );
}, []);

    // ----------------------------
    // SOCKET CONNECTION (Create once, persist)
    // ----------------------------
    useEffect(() => {
        if (!profile?.user_id) return;

        const token = Cookies.get("auth_token");
        if (!token) return;

        // Validate token format (should be JWT)
        if (token && !token.includes('.')) {
            }

        // Create socket with optimized configuration (persists across page navigation)
        const socket = ensureChatSocket(token);
        if (!socket) return;

        socketRef.current = socket;

        socket.on("connect", () => {
            // Rejoin chat if contact was already selected (using ref to avoid stale closure)
            const currentContactId = selectedContactIdRef.current;
            if (currentContactId) {
                socket.emit("start_chat", { to_user_id: currentContactId });
            }
        });

        socket.on("connect_error", (err) => {
            // Ignore WebSocket-related errors since we're using polling only
            // The error "WebSocket connection to 'ws://localhost:3000/ws' failed" is from
            // React's hot reload system, NOT from Socket.IO
            if (err.message && (
                err.message.includes("WebSocket") ||
                err.message.includes("ws://") ||
                err.message.includes("Invalid frame header") ||
                err.message.includes("/ws")
            )) {
                // This is NOT a Socket.IO error - ignore it
                return;
            }

            // Only show error on first attempt to avoid spam
            if (!socket.reconnecting || socket.reconnectionAttempts === 0) {
                if (err.message && err.message.includes("server error")) {
                    toast.error("Server rejected connection. Check console for details.");
                } else if (err.message && err.message.includes("CORS")) {
                    toast.error("Connection blocked by CORS.");
                } else {
                    }
            }
        });

        socket.on("connected", (data) => {
            });

        socket.on("joined", (data) => {
        });

        socket.on("disconnect", (reason) => {
            failActiveUploadsOnDisconnect(reason);
        });

        // ----------------------------
        // RECEIVE MESSAGE
        // ----------------------------
        socket.on("new_message", (payload) => {
            // Extract payload data
            const data = payload?.data || payload;

            // Extract message fields
            const { senderId } = getMessageIdentity(data);
            
            const messageId = data?.message_id || data?.id || `server_${Date.now()}`;
            const isFromCurrentUser = isCurrentUserMessage(data);

// 🔥 WhatsApp-style reorder
if (!isFromCurrentUser) {
    moveContactToTop(senderId, data?.created_at || new Date().toISOString());
}

            // Extract text and file info
            let messageText = toDisplayText(data?.message ?? data?.msg ?? data?.text ?? "", "");
            const fileUrl = data?.file || data?.file_url || data?.file_path || data?.url || null;
            let fileName = data?.filename || data?.file_name || data?.name || (fileUrl ? fileUrl.split('/').pop() : null);
            if (!fileName && messageText && /\.(mp4|webm|mov|avi|mkv|m4v|png|jpe?g|gif|webp|pdf|docx?)(\?.*)?$/i.test(messageText.trim())) {
                fileName = messageText.trim();
            }
            const mime = data?.mime_type || data?.mime || data?.file_mime || null;
            const fileSize = data?.file_size_str || data?.file_size || data?.size || null;
            const shareType = data?.share_type || null;
            const viewedBy = Array.isArray(data?.viewed_by) ? data.viewed_by : [];
            const fileDeleted = Boolean(data?.file_deleted) || (shareType === "sharetoview" && !fileUrl);
            const thumbnailImage = data?.thumbnail_image || data?.thumbnail_path || null;

            // Determine message type (align with group chat)
            const socketMessageType = data?.message_type || data?.type;
            let type = "text";
            if (socketMessageType === "file" && fileUrl) {
                const mimeValue = String(mime || "").toLowerCase();
                if (mimeValue.startsWith("image")) type = "image";
                else if (mimeValue.startsWith("audio")) type = "audio";
                else if (mimeValue.startsWith("video")) type = "video";
                else type = "document";
            } else {
                type = resolveMessageMediaType({
                    type: data?.type,
                    messageType: data?.message_type,
                    mime,
                    fileName,
                    fileUrl,
                });
            }
            if (fileName || fileUrl) {
                if (!messageText) messageText = fileName || "[file]";
            }

            if (!isMessageForActiveChat(data)) {
                return;
            }

            setMessages(prev => {
                const existingIndex = prev.findIndex((m) => String(m.id) === String(messageId));
                if (existingIndex >= 0) {
                    const merged = mergeSocketMessageUpdate(prev[existingIndex], {
                        messageText,
                        type,
                        fileName,
                        fileUrl,
                        mime,
                        fileSize,
                        thumbnailImage,
                        shareType,
                        viewedBy,
                        fileDeleted,
                        data,
                    });
                    const next = [...prev];
                    next[existingIndex] = merged;
                    return next;
                }

                const isOutgoingMedia =
                    isFromCurrentUser &&
                    (fileName || fileUrl || (type !== "text" && type !== "audio"));

                if (isOutgoingMedia) {
                    return mergeOutgoingFileEchoOrSkip(prev, {
                        messageId,
                        messageText,
                        type,
                        fileName,
                        fileUrl,
                        mime,
                        fileSize,
                        thumbnailImage,
                        shareType,
                        viewedBy,
                        fileDeleted,
                        data,
                    });
                }

                if (isFromCurrentUser && !fileName && messageText) {
                    const matching = findMatchingOptimisticTextMessage(prev, messageText);
                    if (matching && isWithinDeducationWindow(matching.id, DEDUP_WINDOW_TEXT)) {
                        return replaceOptimisticMessage(prev, matching, messageId, data);
                    }
                }

                const incoming = {
                    id: messageId,
                    text: messageText,
                    type,
                    isUser: isFromCurrentUser,
                    created_at: data?.created_at || data?.timestamp || null,
                    time: data?.custom_time || data?.time || new Date().toISOString(),
                    fileName,
                    fileUrl: fileUrl ? resolveServerFileUrl(fileUrl) : fileUrl,
                    fileUrlRaw: fileUrl || undefined,
                    mime,
                    fileSize,
                    thumbnail_image: thumbnailImage,
                    shareType,
                    viewedBy,
                    fileDeleted,
                    original: data,
                };

                return [...prev, incoming];
            });

            // Mark as read if from other user
            if (!isFromCurrentUser) {
                setTimeout(() => {
                    autoMarkAsRead();
                }, AUTO_MARK_READ_DELAY);

                // If this chat is currently open, also call mark_read API
                if (String(senderId) === String(selectedContactIdRef.current)) {
                    markedReadRef.current[String(senderId)] = false;
                    const activeFriend =
                        contactsRef.current.find(c => String(c.user_id) === String(senderId)) ||
                        contactDetailsRef.current.find(c => String(c.user_id) === String(senderId));
                    if (activeFriend) {
                        markMessagesRead(activeFriend);
                    }
                }
            }
        });

        // ----------------------------
        // MARK READ EVENT (Real-time read status updates)
        // ----------------------------
        socket.on("marked_read", (data) => {
            // Mark all messages from the selected contact as read
            if (data?.status === "1") {
                setMessages(prev =>
                    prev.map(m => {
                        const msgSender = String(m.original?.sender_id || m.original?.from_user_id || m.sender_id || m.from_user_id);
                        const msgReceiver = String(m.original?.receiver_id || m.original?.to_user_id || m.receiver_id || m.to_user_id);
                        const isFromFriend = msgSender === String(selectedContactId);
                        const isToMe = msgReceiver === String(profile?.user_id);
                        const isIncoming = !m.isUser;
                        if ((isFromFriend && isToMe) || isIncoming) {
                            return { ...m, read: true, original: { ...m.original, read: true } };
                        }
                        return m;
                    })
                );
            }
        });

        socket.on("error", (error) => {
            });

        // ----------------------------
        // 🔥 LIVE CHAT LIST UPDATES (WhatsApp-style)
        // ----------------------------
        socket.on("chat_list_updated", (payload) => {
            const conversation = payload?.conversation || payload?.data?.conversation || payload;
            
            if (!conversation || !conversation.user_id) {
                return;
            }

            const userId = conversation.user_id;
            const lastMessage = toDisplayText(conversation.last_message, "");
            const lastMessageTime = conversation.last_message_time || new Date().toISOString();
            const messageType = conversation.message_type || "text";
            const unreadCount = conversation.unread_count || 0;

            setContacts((prev) => {
                const existingIndex = prev.findIndex(c => String(c.user_id) === String(userId));

                // Normalize is_friend flag to numeric 0/1 so UI logic stays stable
                const normalizeIsFriend = (value, fallback) => {
                    if (value === undefined || value === null) return fallback;
                    const n = Number(value);
                    return Number.isNaN(n) ? fallback : n;
                };
                
                // Create updated contact object
                const updatedContact = existingIndex >= 0 
                    ? {
                        ...prev[existingIndex],
                        last_message: lastMessage,
                        last_message_time: lastMessageTime,
                        message_type: messageType,
                        unread_count: String(userId) === String(selectedContactIdRef.current) 
                            ? 0 
                            : unreadCount,
                        // Preserve other fields from server response
                        ...(conversation.name && { name: conversation.name }),
                        ...(conversation.full_name && { full_name: conversation.full_name }),
                        ...(conversation.profile_image && { profile_image: conversation.profile_image }),
                        // Keep existing is_friend unless backend explicitly flips it; coerce to number.
                        // If we already marked as friend, do not downgrade to 0 from chat_list update noise.
                        is_friend: (() => {
                            const incoming = normalizeIsFriend(conversation.is_friend, prev[existingIndex]?.is_friend ?? 1);
                            const previous = normalizeIsFriend(prev[existingIndex]?.is_friend, 1);
                            if (previous === 1 && incoming === 0) return 1;
                            return incoming;
                        })(),
                    }
                    : {
                        // New conversation - add it
                        user_id: userId,
                        name: conversation.name || conversation.full_name || "Unknown",
                        full_name: conversation.full_name || conversation.name || "Unknown",
                        phone_number: conversation.phone_number || "",
                        country_code: conversation.country_code || "",
                        email: conversation.email || "",
                        profile_image: conversation.profile_image || "",
                        last_message: lastMessage,
                        last_message_time: lastMessageTime,
                        message_type: messageType,
                        unread_count: String(userId) === String(selectedContactIdRef.current) 
                            ? 0 
                            : unreadCount,
                        // Default to friend if backend omits flag so UI doesn't show add-friend
                        is_friend: normalizeIsFriend(conversation.is_friend, 1),
                    };

                // WhatsApp-style: Move to top
                if (existingIndex >= 0) {
                    // Remove from current position and add to top
                    const newList = prev.filter((_, i) => i !== existingIndex);
                    return [updatedContact, ...newList];
                } else {
                    // Add new conversation to top
                    return [updatedContact, ...prev];
                }
            });
        });

        // ----------------------------
        // 🔥 CONTACTS LIST UPDATES + FRIEND STATUS
        // ----------------------------
        const onContactSyncEvent = (payload) => {
            const contact = extractContactFromSocketPayload(payload);
            if (!contact?.user_id) return;

            setContactDetails((prev) => applyContactListPatch(prev, contact));
            setContacts((prev) => applyContactListPatch(prev, contact));
        };

        socket.on("contacts_list_updated", onContactSyncEvent);
        CONTACT_SYNC_SOCKET_EVENTS.forEach((eventName) => {
            if (eventName !== "contacts_list_updated") {
                socket.on(eventName, onContactSyncEvent);
            }
        });

        const onAnyContactEvent = (eventName, payload) => {
            if (CONTACT_SYNC_SOCKET_EVENTS.includes(eventName)) return;
            if (!isContactSyncEventName(eventName)) return;
            onContactSyncEvent(payload);
        };
        socket.onAny(onAnyContactEvent);

        // ----------------------------
        // 🔥 GROUP LIST UPDATES
        // ----------------------------
        socket.on("group_list_updated", (payload) => {
            const group = payload?.group || payload?.data?.group || payload;
            if (!group || !group.group_id) return;

            upsertGroupInChatList(group);
        });

        // ----------------------------
        // 🔥 MESSAGE READ RECEIPTS
        // ----------------------------
        socket.on("marked_read", (payload) => {
            // Update message read status if needed
            const data = payload?.data || payload;
            const userId = data?.user_id;
            const messageId = data?.message_id || data?.up_to_message_id;

            if (userId) {
                // Mark messages as read in UI
                setMessages((prev) => 
                    prev.map(m => {
                        if (m.isUser && String(m.receiverId) === String(userId)) {
                            return { ...m, read: true };
                        }
                        return m;
                    })
                );
            }
        });

        socket.on("image_viewed", (payload) => {
            const data = payload?.data || payload;
            const messageId = data?.message_id;
            if (!messageId) return;
            setMessages((prev) => prev.map((m) => {
                if (String(m.id) !== String(messageId)) return m;
                const nextViewed = Array.isArray(m.viewedBy) ? [...m.viewedBy] : [];
                if (profile?.user_id && !nextViewed.some(id => String(id) === String(profile.user_id))) {
                    nextViewed.push(profile.user_id);
                }
                return {
                    ...m,
                    viewedBy: nextViewed,
                    fileDeleted: data?.file_deleted || m.fileDeleted,
                    original: { ...m.original, viewed_by: nextViewed, file_deleted: data?.file_deleted || m.fileDeleted },
                };
            }));
        });

        // Detach listeners only — keep socket alive for background uploads (WhatsApp-style)
        return () => {
            socket.off("connect");
            socket.off("connect_error");
            socket.off("connected");
            socket.off("joined");
            socket.off("disconnect");
            socket.off("new_message");
            socket.off("marked_read");
            socket.off("image_viewed");
            socket.off("error");
            socket.off("chat_list_updated");
            socket.off("contacts_list_updated", onContactSyncEvent);
            CONTACT_SYNC_SOCKET_EVENTS.forEach((eventName) => {
                if (eventName !== "contacts_list_updated") {
                    socket.off(eventName, onContactSyncEvent);
                }
            });
            socket.offAny(onAnyContactEvent);
            socket.off("group_list_updated");
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile?.user_id]); // Only recreate when profile.user_id changes, NOT on contact change

    // ----------------------------
    // CHECK URL PARAMS FOR AUTO-SELECT
    // ----------------------------
    useEffect(() => {
        if (routeUserId) {
            setAutoSelectPending(routeUserId);
        }
    }, [routeUserId]);

    // ----------------------------
    // LOAD PROFILE + CONTACTS
    // ----------------------------
    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = Cookies.get("auth_token");

                if (!token) {
                    toast.dismiss();
                    toast.error("Session expired. Please log in again.");
                    return (window.location.href = "/login");
                }

                const [profileRes, birthdayRes, friendReqRes] =
                    await Promise.all([
                        apiGet("/profile", token),
                        apiGet("/birthdays/getall", token),
                        apiGet("/friend-request/list", token),
                    ]);
                setProfile(profileRes?.profile || {});

                const birthdays = Array.isArray(birthdayRes?.birthdays) ? birthdayRes.birthdays : [];
                const anniversaries = Array.isArray(birthdayRes?.anniversaries) ? birthdayRes.anniversaries : [];
                const combined = [...birthdays, ...anniversaries].map((item) => {
                    const rawUrl = item?.profile_image_url || item?.profile_image || "";
                    const avatar = rawUrl && rawUrl.startsWith("http")
                        ? rawUrl
                        : rawUrl
                            ? `${process.env.REACT_APP_API_BASE_URL}${rawUrl}`
                            : "";
                    const name = item?.full_name || `${item?.first_name || ""} ${item?.last_name || ""}`.trim();
                    return {
                        user_id: item?.user_id || item?.friend_user_id || item?.id || null,
                        name: name || "Unknown",
                        avatar,
                        type: item?.date_of_birth ? "birthday" : "anniversary",
                        original: item
                    };
                });

                setBirthday(combined);
                setFriendRes(friendReqRes || {});
            } catch (err) {
                toast.error(err.message);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = Cookies.get("auth_token");

                if (!token) {
                    toast.dismiss();
                    toast.error("Session expired. Please log in again.");
                    return (window.location.href = "/login");
                }
                const chatList = await apiPost("/get_chat_list", {}, token);
                setContacts(Array.isArray(chatList) ? chatList : []);
            } catch (err) {
                toast.error(err.message);
            } finally {
                setChatListLoaded(true);
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Fetch full contact details (used when navigating with URL id)
    useEffect(() => {
        const fetchDetails = async () => {
            try {
                const token = Cookies.get("auth_token");

                if (!token) {
                    toast.dismiss();
                    toast.error("Session expired. Please log in again.");
                    return (window.location.href = "/login");
                }

                const contactRes = await apiGet("/get-contacts", token);
                const contactsList = contactRes?.contacts || [];
                setContactDetails(Array.isArray(contactsList) ? contactsList : []);
            } catch (err) {
                toast.error(err.message);
            }
        };

        fetchDetails();
    }, []);

    // ----------------------------
    // AUTO-SELECT CONTACT FROM URL PARAM
    // ----------------------------
    useEffect(() => {
        if (autoSelectPending) {
            // Prefer detailed contacts to ensure full data
            const sourceList = contactDetails.length > 0 ? contactDetails : contacts;
            const contactExists = sourceList.find(c => String(c.user_id) === String(autoSelectPending));
            if (contactExists) {
                handleContactSelect(autoSelectPending);
                setAutoSelectPending(null);
                return;
            }

            // If no contact matched, attempt to match a group id and open group details
            const groupExists = sourceList.find(c => String(c.group_id) === String(autoSelectPending));
            if (groupExists) {
                // When navigating via URL (e.g. /chat/:id) prefer opening the group chat view
                setSelectedGroupId(String(autoSelectPending));
                setShowGroupDetails(false);
                setAutoSelectPending(null);
                return;
            }

            if (isAdminSupportContact(autoSelectPending) && chatListLoaded) {
                handleContactSelect(autoSelectPending);
                setAutoSelectPending(null);
            }
        }
    }, [autoSelectPending, contacts, contactDetails, chatListLoaded]);

    // ------------------------------------
    // AUTO MARK AS READ
    // ------------------------------------
    const autoMarkAsRead = () => {
        if (!selectedContactId) {
            return;
        }
        if (!socketRef.current) {
            return;
        }
        if (!socketRef.current.connected) {
            return;
        }

        const payload = { to_user_id: selectedContactId };
        socketRef.current.emit("mark_read", payload, (response) => {
            if (response && response.error) {
                } else {
                if (response !== undefined) {
                    }
                setMessages(prev =>
                    prev.map(m =>
                        !m.isUser ? { ...m, read: true } : m
                    )
                );
            }
        });
    };

    // Always mark as read when there are unread messages from others
    useEffect(() => {
        if (messages.some(m => !m.isUser && !m.read)) {
            setTimeout(() => {
                autoMarkAsRead();
            }, AUTO_MARK_READ_DELAY);
        }
    }, [messages, selectedContactId]);

    // ------------------------------------
    // LOAD CHAT HISTORY WHEN SELECT CONTACT
    // ------------------------------------
    const loadChatHistory = async (friendId) => {
        try {
            const token = Cookies.get("auth_token");
            if (!token) {
                return;
            }

            const friend = contacts.find((f) => f.user_id === friendId)
                || contactDetails.find((f) => f.user_id === friendId)
                || (isAdminSupportContact(friendId)
                    ? {
                        user_id: friendId,
                        full_name: "PhoneBookPlus Support",
                        name: "PhoneBookPlus Support",
                        type: "broadcast_msg",
                    }
                    : null);
            if (!friend) {
                return;
            }
            const isAdminContact = isAdminSupportContact(friend?.user_id);
            // Send as application/x-www-form-urlencoded as required
            const params = new URLSearchParams();
            params.append("user_id", friend.user_id);
            if (!isAdminContact && friend.phone_number) {
                const receiverPhone = String(friend.phone_number).startsWith("+")
                    ? String(friend.phone_number)
                    : `+91${friend.phone_number}`;
                params.append("receiver_phone", receiverPhone);
            }
            params.append("limit", "1000");
            params.append("direction", "asc");

            const res = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/get_chat_message`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/x-www-form-urlencoded"
                    },
                    body: params.toString()
                }
            );

            const data = await res.json();
            // Compute a friendly offline status when server indicates text-only status
            try {
                const fs = data?.friend_status || null;
                let computedStatus = toDisplayText(fs?.text, "");

                const textStatusFlag = fs?.textstatus ?? fs?.text_status ?? null;
                const hasText = fs && fs.text && String(fs.text).trim().length > 0;

                if ((String(textStatusFlag) === "1" || textStatusFlag === 1) && !hasText) {
                    // Prefer explicit ISO `until` if provided, otherwise combine `date` + `time` if present
                    let iso = fs?.until || (fs?.date && fs?.time ? `${fs.date}T${fs.time}Z` : null);

                    // Fallback: if only date provided, use date
                    if (!iso && fs?.date) iso = fs.date;

                    let dt = iso ? new Date(iso) : null;
                    if (dt && !isNaN(dt)) {
                        // Format in IST (Asia/Kolkata)
                        // Use the browser's locale and timezone so formatting is dynamic per user
                        const userLocale = (typeof navigator !== 'undefined' && navigator.language) ? navigator.language : 'en-US';
                        const userTimeZone = (typeof Intl !== 'undefined' && Intl.DateTimeFormat) ? (Intl.DateTimeFormat().resolvedOptions().timeZone || undefined) : undefined;

                        const dateOpts = { day: '2-digit', month: 'short', year: 'numeric', ...(userTimeZone ? { timeZone: userTimeZone } : {}) };
                        const timeOpts = { hour: '2-digit', minute: '2-digit', hour12: true, ...(userTimeZone ? { timeZone: userTimeZone } : {}) };

                        let datePart = new Intl.DateTimeFormat(userLocale, dateOpts).format(dt).toUpperCase(); // e.g., 21 FEB 2026
                        let timePart = new Intl.DateTimeFormat(userLocale, timeOpts).format(dt); // e.g., 09:00 PM

                        computedStatus = `Offline Until ${datePart}, ${timePart} IST. Delivery and responses may be delayed`;
                    } else {
                        computedStatus = 'Offline. Delivery and responses may be delayed';
                    }
                }

                setStatus(toDisplayText(computedStatus, ""));
            } catch (err) {
                setStatus(toDisplayText(data?.friend_status?.text, ""));
            }
            // Handle different response structures
            // The API might return: data.data (array) or data.data[0].messages or data.messages
            let messagesArr = [];

            if (Array.isArray(data?.data)) {
                // Check if first item has messages array
                if (data.data[0]?.messages && Array.isArray(data.data[0].messages)) {
                    messagesArr = data.data[0].messages;
                } else {
                    // Messages are directly in data.data array
                    messagesArr = data.data;
                }
            } else if (Array.isArray(data?.messages)) {
                messagesArr = data.messages;
            } else if (Array.isArray(data?.data?.messages)) {
                messagesArr = data.data.messages;
            } else if (data?.data && Array.isArray(data.data)) {
                messagesArr = data.data;
            }

            // Format messages to match component structure
            const formatted = messagesArr
                .map((m, idx) => {
                    // Use exact field names from API response
                    const messageId = m.message_id || m.id || `msg_${idx}_${Date.now()}`;
                        const { senderId } = getMessageIdentity(m);
                    // Basic text fallback
                    let messageText = toDisplayText(m.message ?? m.msg ?? m.body ?? "", "");

                    // File detection for history items
                    const fileName = m.filename || m.file_name || m.name || null;
                    const fileUrl = m.file || m.file_url || m.file_path || m.url || null;
                    const mime = m.mime_type || m.mime || m.file_mime || null;
                    const fileSize = m.file_size_str || m.file_size || m.size || null;
                    const shareType = m.share_type || null;
                    const viewedBy = Array.isArray(m.viewed_by) ? m.viewed_by : [];
                    const fileDeleted = Boolean(m.file_deleted) || (shareType === "sharetoview" && !fileUrl);
                    const thumbnailImage = m.thumbnail_image || m.thumbnail_path || null;
                    const isFromCurrentUser = isCurrentUserMessage(m);
                    const customTime = m.custom_time || null;
                    const createdAt = m.created_at || m.timestamp || null;

                    // Pass raw ISO timestamp to child component for proper formatting
                    let displayTime = customTime || createdAt || new Date().toISOString();

                    // If file present, derive type and show filename as text fallback
                    let type = resolveMessageMediaType({
                        type: m.type,
                        messageType: m.message_type,
                        mime,
                        fileName,
                        fileUrl,
                    });
                    if (fileName || fileUrl) {
                        if (!messageText) messageText = fileName || "[file]";
                    }

                    return {
                        id: messageId,
                        text: messageText,
                        type,
                        isUser: isFromCurrentUser,
                        time: displayTime,
                        created_at: createdAt,
                        read: m.read || false,
                        fileName,
                        fileUrl: fileUrl ? resolveServerFileUrl(fileUrl) : fileUrl,
                        fileUrlRaw: fileUrl || undefined,
                        mime,
                        fileSize,
                        thumbnail_image: thumbnailImage,
                        shareType,
                        viewedBy,
                        fileDeleted,
                        // Keep original data for reference
                        original: m,
                    };
                })
                .sort((a, b) => getMessageTimestamp(a) - getMessageTimestamp(b));

            const merged = mergeMessagesWithPending(formatted, friendId).map(attachCancelHandler);
            setMessages(merged);

            if (formatted.length === 0) {
                }

            // Automatically mark messages as read after loading chat history
            // Use a small delay to ensure socket is ready
            setTimeout(() => {
                autoMarkAsRead();
                markMessagesRead(friend);
            }, MARK_READ_DELAY);
        } catch (err) {
            toast.error("Failed to load chat history: " + (err.message || "Unknown error"));
            setMessages([]); // Clear messages on error
        }
    };

    const historyLoadedForRef = useRef(null);

    useEffect(() => {
        historyLoadedForRef.current = null;
    }, [selectedContactId]);

    // Load history when contact is selected; retry when contact metadata arrives (deep links).
    // Skips reload on chat_list updates once history for this contact is already loaded.
    useEffect(() => {
        if (!selectedContactId || !profile?.user_id) return;

        const friend =
            contacts.find((f) => f.user_id === selectedContactId) ||
            contactDetails.find((f) => f.user_id === selectedContactId);
        if (!friend) return;

        if (historyLoadedForRef.current === String(selectedContactId)) return;

        historyLoadedForRef.current = String(selectedContactId);
        loadChatHistory(selectedContactId);
    }, [selectedContactId, profile?.user_id, contacts, contactDetails]);

    // Sync background upload state when returning to chat or upload completes elsewhere
    useEffect(() => {
        return subscribeUploadEvents((event, { contactId, tempId, message }) => {
            if (String(contactId) !== String(selectedContactIdRef.current)) return;

            if (event === "removed") {
                setMessages((prev) => prev.filter((m) => m.tempId !== tempId));
                return;
            }

            if (event === "added") {
                setMessages((prev) => {
                    if (prev.some((m) =>
                        m.tempId === tempId ||
                        m._clientUploadId === tempId ||
                        String(m.id) === String(tempId)
                    )) {
                        return prev;
                    }
                    return [...prev, attachCancelHandler(message)];
                });
                return;
            }

            setMessages((prev) => {
                const idx = prev.findIndex((m) =>
                    m.tempId === tempId ||
                    m._clientUploadId === tempId ||
                    String(m.id) === String(tempId)
                );
                const withHandlers = attachCancelHandler(message);
                if (idx === -1) return prev;
                const next = prev.map((m, i) => (i === idx ? withHandlers : m));
                return next;
            });
        });
    }, [attachCancelHandler]);

    // ------------------------------------
    // JOIN CHAT ROOM WHEN CONTACT SELECTED
    // ------------------------------------
    useEffect(() => {
        // Update refs whenever selection changes
        selectedContactIdRef.current = selectedContactId;
        selectedGroupIdRef.current = selectedGroupId;
        if (selectedContactId) {
            markedReadRef.current[String(selectedContactId)] = false;
        }

        if (!selectedContactId || !socketRef.current) return;

        // Wait for socket to be connected before joining
        if (socketRef.current.connected) {
            socketRef.current.emit("start_chat", { to_user_id: selectedContactId });
        } else {
            // If not connected, wait for connection then join
            const onConnect = () => {
                const contactId = selectedContactIdRef.current;
                if (contactId) {
                    socketRef.current.emit("start_chat", { to_user_id: contactId });
                }
                socketRef.current.off("connect", onConnect);
            };
            socketRef.current.on("connect", onConnect);
        }
    }, [selectedContactId, selectedGroupId]);

    // ------------------------------------
    // CLICK CONTACT
    // ------------------------------------
    // Track last selected contact id to allow reload on same contact click
    const [lastSelectedContactId, setLastSelectedContactId] = useState(null);
    const handleGroupUnmoved = useCallback((groupId) => {
        setContacts((prev) => prev.filter((c) => String(c.group_id) !== String(groupId)));
        if (String(selectedContactIdRef.current) === String(groupId)) {
            setSelectedContactId(null);
            setMessages([]);
            if (window.innerWidth < 768) setShowSidebar(true);
        }
    }, []);

    const handleContactSelect = (id) => {
        // If this contact is not in chat contacts, but is in contactDetails, merge it in
        setContacts(prev => {
            const exists = prev.some(c => String(c.user_id) === String(id));
            if (!exists) {
                const full = contactDetails.find(c => String(c.user_id) === String(id));
                if (full) {
                    // Normalize is_friend: if missing but in phonebook, default to 1
                    let isFriend = full.is_friend;
                    if (isFriend === undefined || isFriend === null) {
                        isFriend = full.phone_book_user === 1 ? 1 : 0;
                    }
                    return [
                        {
                            ...full,
                            is_friend: isFriend,
                            last_message: '',
                            last_message_time: new Date().toISOString(),
                            unread_count: 0,
                        },
                        ...prev
                    ];
                }
                if (isAdminSupportContact(id)) {
                    return [
                        {
                            user_id: id,
                            full_name: "PhoneBookPlus Support",
                            name: "PhoneBookPlus Support",
                            type: "broadcast_msg",
                            is_friend: 1,
                            last_message: '',
                            last_message_time: new Date().toISOString(),
                            unread_count: 0,
                        },
                        ...prev
                    ];
                }
            }
            return prev;
        });
        historyLoadedForRef.current = null;
        // If same contact is selected again, force reload by clearing and setting again
        if (selectedContactId === id) {
            setSelectedContactId(null);
            setTimeout(() => setSelectedContactId(id), 0);
        } else {
            setSelectedContactId(id);
        }
        setSelectedGroupId(null);  // 🔹 NEW: Deselect any group when user is selected
        setMessages([]);
        setLastSelectedContactId(id);
        if (window.innerWidth < 768) setShowSidebar(false);
    };



    // 🔹 Handle group selection - open in side panel
    const handleSelectGroupDetails = (groupId) => {
        setSelectedGroupId(groupId);
        setSelectedContactId(null);  // Deselect any individual chat
        setShowGroupDetails(false);  // Show chat by default
        if (window.innerWidth < 768) setShowSidebar(false);
    };

    // 🔹 Handle group info icon click - show group details
    const handleGroupInfoClick = (groupId) => {
        setSelectedGroupId(groupId);
        setSelectedContactId(null);  // Deselect any individual chat
        setShowGroupDetails(true);   // Show details panel
        if (window.innerWidth < 768) setShowSidebar(false);
    };



    const handleSendMessage = () => {
        if (!message.trim() || !selectedContactId) return;

        // Check if socket is connected
        if (!socketRef.current || !socketRef.current.connected) {
            toast.error("Not connected to chat server. Please wait...");
            return;
        }

        const payload = {
            to_user_id: selectedContactId,
            message: message.trim(),
            client_time: new Date().toISOString()
        };

        const tempId = Date.now();
        const messageText = payload.message;

        // Optimistic UI - add message immediately
        setMessages(prev => [
            ...prev,
            {
                id: tempId,
                tempId: tempId,  // Mark as optimistic message for deduplication
                text: messageText,
                isUser: true,
                type: "text",
                created_at: new Date().toISOString(),
                time: new Date().toISOString()
            }
        ]);

        // Clear input immediately
        setMessage("");

        // Send via socket
        socketRef.current.emit("send_message", payload, (response) => {
            if (response && response.error) {
                toast.error("Failed to send message");
                // Remove optimistic message on error
                setMessages(prev => prev.filter(m => m.id !== tempId));
            } else {
                }
        });
    };

    const handleViewOnceImage = (message) => {
        if (!message?.id || !selectedContactId || !socketRef.current) return;
        const shareType = message.shareType || message.original?.share_type || "";
        if (shareType !== "sharetoview") return;
        const messageId = message.id;

        // Disable screenshot/screen capture for view-once images
        document.body.style.webkitUserSelect = 'none';
        document.body.style.userSelect = 'none';
        if (document.body.style.setProperty) {
            document.body.style.setProperty('-webkit-user-select', 'none', 'important');
        }
        
        socketRef.current.emit("mark_image_viewed", {
            user_id: selectedContactId,
            message_id: messageId,
        });
        setMessages((prev) => prev.map((m) => {
            if (String(m.id) !== String(messageId)) return m;
            const nextViewed = Array.isArray(m.viewedBy) ? [...m.viewedBy] : [];
            if (profile?.user_id && !nextViewed.some(id => String(id) === String(profile.user_id))) {
                nextViewed.push(profile.user_id);
            }
            return {
                ...m,
                viewedBy: nextViewed,
                original: { ...m.original, viewed_by: nextViewed },
            };
        }));
    };

    // Share an existing message (text or file) to up to 5 contacts
    const handleShareMessage = async (messageToShare, targetContactIds = [], contactsList = contacts) => {
        const chatSocket = getChatSocket();
        return shareMessageToContacts({
            socket: chatSocket,
            messageToShare,
            targetContactIds,
            contactsList,
        });
    };


    // ------------------------------------
    // VOICE + ATTACHMENT HANDLERS
    // ------------------------------------
    // Handles file selection UI from AttachmentModal. Opens a hidden input
    // with the appropriate `accept` / `capture` attributes depending on type.
    const handleAttachPick = (type) => {
        if (!selectedContactId) {
            toast.error("Select a contact first");
            setShowAttachModal(false);
            setShowVoiceAttachModal(false);
            return;
        }

        const input = fileInputRef.current;
        if (!input) {
            setShowAttachModal(false);
            setShowVoiceAttachModal(false);
            return;
        }

        // Set accept attribute based on type
        if (type === "photo") {
            input.setAttribute("accept", "image/*");
            input.removeAttribute("capture");
            } else if (type === "camera") {
            input.setAttribute("accept", "image/*");
            input.setAttribute("capture", "environment");
            } else if (type === "document") {
            input.setAttribute("accept", "*/*");
            input.removeAttribute("capture");
            } else {
            input.setAttribute("accept", "*/*");
            input.removeAttribute("capture");
            }

        // Reset input value to allow selecting the same file again
        input.value = "";

        // For camera, some browsers need a short delay before triggering click
        if (type === "camera") {
            setTimeout(() => {
                input.click();
                setShowAttachModal(false);
                setShowVoiceAttachModal(false);
            }, 100);
        } else {
            input.click();
            setTimeout(() => {
                setShowAttachModal(false);
                setShowVoiceAttachModal(false);
            }, 100);
        }
    };

    const handleFileSelect = (fileOrFiles, fileTypeHint, shareTypeOverride = "") => {
        if (!fileOrFiles) return;

        const files = Array.isArray(fileOrFiles)
            ? fileOrFiles
            : Array.from(fileOrFiles instanceof FileList ? fileOrFiles : [fileOrFiles]);

        const imageQueue = [];

        files.forEach((file) => {
            if (!file) return;
            const isImageHint = fileTypeHint === "photo" || fileTypeHint === "camera";
            const isImageMime = file.type?.startsWith("image/");
            const ext = file.name?.split(".").pop()?.toLowerCase();
            const imageExts = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];
            const videoExts = ["mp4", "webm", "mov", "avi", "mkv", "ogg"];
            const isImageExt = imageExts.includes(ext);
            const isVideoExt = videoExts.includes(ext);
            const isImage = isImageHint || isImageMime || isImageExt;

            if (isImage) {
                imageQueue.push(file);
            } else {
                // Videos and other files are sent directly without prompting
                handleFileInput(file, fileTypeHint, shareTypeOverride);
            }
        });

        if (imageQueue.length > 0) {
            imageQueue.forEach((file) => {
                handleFileInput(file, fileTypeHint, shareTypeOverride);
            });
        }
    };
    // Reads a File and emits it over socket.io as raw bytes (Uint8Array).
    const handleFileInput = async (file, fileTypeHint, shareTypeOverride) => {
        let previewUrl = null;
        const fileType = resolveFileType(file, fileTypeHint);
        const contactIdForUpload = selectedContactId;
        const chatSocket = getChatSocket();
        const logUpload = (phase, extra = {}) => {
            if (!shouldLogMediaUpload(fileType, file)) return;
            logMediaUpload("1-on-1", phase, file, {
                fileType,
                fileTypeHint,
                contactId: contactIdForUpload,
                socketConnected: Boolean(chatSocket?.connected),
                socketId: chatSocket?.id ?? null,
                apiBaseUrl: process.env.REACT_APP_API_BASE_URL ?? null,
                ...extra,
            });
        };

        logUpload("start", { shareTypeOverride });

        // Validation checks
        if (!file) {
            logUpload("aborted-no-file");
            return;
        }

        if (!selectedContactId) {
            toast.error("Select a contact first");
            logUpload("aborted-no-contact");
            return;
        }

        if (!chatSocket || !chatSocket.connected) {
            toast.error("Not connected to server");
            logUpload("aborted-not-connected");
            return;
        }

        const uploadDedupeKey = `${contactIdForUpload}:${String(file.name || "").toLowerCase()}:${file.size}`;
        const lastUploadAt = recentUploadKeysRef.current.get(uploadDedupeKey);
        if (lastUploadAt && Date.now() - lastUploadAt < 5000) {
            logUpload("aborted-duplicate-trigger", { uploadDedupeKey });
            return;
        }
        recentUploadKeysRef.current.set(uploadDedupeKey, Date.now());

        // No client-side file size limit — uploads are validated by the server only.

        try {
            setIsUploading(true);
            setUploadProgress(0);
            const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const formatFileSize = (bytes) => {
                if (bytes < 1024) return bytes + " B";
                if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
                return (bytes / (1024 * 1024)).toFixed(1) + " MB";
            };
            const shareTypeToSend = (fileType === "image" || fileType === "video") ? (shareTypeOverride ?? "") : "";
            previewUrl = (fileType === "image" || fileType === "video") ? URL.createObjectURL(file) : null;
            const optimisticMessage = {
                id: tempId,
                text: file.name,
                type: fileType,
                isUser: true,
                time: new Date().toISOString(),
                fileName: file.name,
                fileUrl: previewUrl,
                mime: file.type || undefined,
                fileSize: formatFileSize(file.size),
                _rawFileSize: file.size,
                _normalizedFileName: String(file.name || "").toLowerCase().replace(/[^a-z0-9]+/g, ""),
                uploading: true,
                tempId,
                _clientUploadId: tempId,
                uploadProgress: 0,
                uploadStartedAt: Date.now(),
                shareType: shareTypeToSend,
            };
            setMessages((prev) => {
                if (prev.some((m) => m.tempId === tempId || m._clientUploadId === tempId || m.id === tempId)) {
                    return prev;
                }
                return [
                    ...prev,
                    {
                        ...optimisticMessage,
                        onCancelUpload: () => handleCancelUpload(tempId, contactIdForUpload),
                    },
                ];
            });
            registerActiveUpload(contactIdForUpload, tempId, file, optimisticMessage, fileType);
            mirrorUploadStarted({
                tempId,
                kind: "dm",
                targetId: contactIdForUpload,
                file,
                fileType,
                shareType: shareTypeToSend,
                previewUrl,
            });
            logUpload("reading-file", { tempId, uploadStartedAt: optimisticMessage.uploadStartedAt });

            // Use FileReader with progress tracking
            const reader = new FileReader();
            let lastLoggedReadProgress = -1;
            let lastMirroredProgress = -1;

            reader.onprogress = (e) => {
                if (e.lengthComputable) {
                    const pct = Math.round((e.loaded / e.total) * 100);
                    // Do not setMessages / setUploadProgress on every tick — that remounts
                    // nested message components and restarts the round loader (flicker).
                    if (pct - lastMirroredProgress >= 10 || pct >= 100) {
                        lastMirroredProgress = pct;
                        mirrorUploadProgress(tempId, pct, "reading");
                    }
                    const milestone = Math.floor(pct / 25) * 25;
                    if (milestone > lastLoggedReadProgress && milestone > 0) {
                        lastLoggedReadProgress = milestone;
                        logUpload("read-progress", {
                            tempId,
                            percent: milestone,
                            loadedBytes: e.loaded,
                            totalBytes: e.total,
                        });
                    }
                }
            };

            reader.onerror = () => {
                setIsUploading(false);
                setUploadProgress(0);
                toast.error("Failed to read file");
                logUpload("read-error", {
                    tempId,
                    error: reader.error?.message || String(reader.error),
                });
                markOptimisticUploadFailed(contactIdForUpload, tempId, "Failed to read file");
            };

            reader.onload = () => {
                try {
                    const bytes = new Uint8Array(reader.result);
                    const emitStartedAt = Date.now();
                    mirrorUploadProgress(tempId, 100, "uploading");
                    patchPendingUpload(contactIdForUpload, tempId, { uploadProgress: 100 }, { notify: false });
                    const payload = {
                        to_user_id: contactIdForUpload,
                        filename: file.name,
                        mime_type: file.type || undefined,
                        caption: "",
                        file: bytes,
                        share_type: shareTypeToSend,
                        client_time: new Date().toISOString(),
                    };
                    logUpload("emit-send_file", {
                        tempId,
                        event: "send_file",
                        byteLength: bytes.length,
                        shareTypeToSend,
                        emitStartedAt: new Date(emitStartedAt).toISOString(),
                        payload: summarizeSocketPayload(payload),
                    });
                    chatSocket.emit("send_file", payload, (ack) => {
                        const ackElapsedMs = Date.now() - emitStartedAt;
                        clearActiveUpload(tempId);
                        setIsUploading(false);
                        setUploadProgress(0);

                        logUpload("ack-received", {
                            tempId,
                            ackElapsedMs,
                            ackIsUndefined: ack === undefined,
                            ack,
                        });

                        if (ack && ack.error) {
                            toast.error(ack.error || "Upload failed");
                            logUpload("ack-error", { tempId, ackElapsedMs, error: ack.error, ack });
                            markOptimisticUploadFailed(contactIdForUpload, tempId, ack.error || "Upload failed");
                            return;
                        }

                        mirrorUploadFinished(tempId);

                        const returnedFile = ack && (ack.file_url || ack.fileUrl || ack.url || ack.path || ack.file_path);
                        const returnedFileName = ack && (ack.filename || ack.fileName || ack.name);
                        const returnedMime = ack && (ack.mime_type || ack.mime || ack.content_type);
                        const returnedThumbnail = ack && (ack.thumbnail_image || ack.thumbnail_path || null);

                        const serverMessageId = ack?.message_id || ack?.id || ack?.data?.message_id || null;

                        if (returnedFile) {
                            const resolved = (typeof returnedFile === 'string') ? returnedFile : (returnedFile.url || returnedFile.path || returnedFile.file_url || '');
                            const resolvedUrl = resolveServerFileUrl(resolved);
                            const patch = {
                                uploading: false,
                                uploadFailed: false,
                                uploadError: undefined,
                                fileUrl: resolvedUrl,
                                fileUrlRaw: resolved,
                                fileName: returnedFileName || file.name,
                                mime: returnedMime || file.type || undefined,
                                thumbnail_image: returnedThumbnail,
                                tempId: undefined,
                                _clientUploadId: undefined,
                                type: fileType,
                                ...(serverMessageId ? { id: serverMessageId } : {}),
                            };
                            patchPendingUpload(contactIdForUpload, tempId, patch, { notify: false });
                            clearPendingUpload(contactIdForUpload, tempId, { notify: false });
                            setMessages(prev => {
                                const next = prev.map(m => {
                                    if (m.id !== tempId && m.tempId !== tempId && m._clientUploadId !== tempId) {
                                        return m;
                                    }
                                    if (m.fileUrl?.startsWith("blob:") && resolvedUrl) {
                                        revokeBlobUrl(m.fileUrl);
                                    }
                                    return {
                                        ...m,
                                        ...patch,
                                        type: fileType,
                                        shareType: m.shareType || shareTypeToSend,
                                    };
                                });
                                return collapseDuplicateOutgoingUploads(next, {
                                    uploadKey: tempId,
                                    serverMessageId,
                                });
                            });
                            logUpload("ack-success-with-url", {
                                tempId,
                                ackElapsedMs,
                                resolvedUrl,
                                serverMessageId,
                            });
                        } else {
                            patchPendingUpload(contactIdForUpload, tempId, {
                                uploading: false,
                                _clientUploadId: undefined,
                                ...(serverMessageId ? { id: serverMessageId } : {}),
                            }, { notify: false });
                            setMessages(prev => {
                                const next = prev.map(m =>
                                    (m.id === tempId || m.tempId === tempId || m._clientUploadId === tempId)
                                        ? {
                                            ...m,
                                            uploading: false,
                                            _clientUploadId: undefined,
                                            ...(serverMessageId ? { id: serverMessageId } : {}),
                                        }
                                        : m
                                );
                                return collapseDuplicateOutgoingUploads(next, {
                                    uploadKey: tempId,
                                    serverMessageId,
                                });
                            });
                            logUpload("ack-success-no-url", {
                                tempId,
                                ackElapsedMs,
                                serverMessageId,
                                note: "Waiting for new_message event to finalize",
                            });
                        }
                    });
                } catch (err) {
                    setIsUploading(false);
                    setUploadProgress(0);
                    toast.error(err.message || "Upload failed");
                    logUpload("emit-error", { tempId, error: err?.message || String(err) });
                    markOptimisticUploadFailed(contactIdForUpload, tempId, err.message || "Upload failed");
                }
            };

            logUpload("read-as-array-buffer-start", { tempId });
            reader.readAsArrayBuffer(file);
        } catch (err) {
            setIsUploading(false);
            setUploadProgress(0);
            toast.error(err.message || "Upload failed");
            if (typeof previewUrl !== "undefined" && previewUrl) URL.revokeObjectURL(previewUrl);
            logUpload("unexpected-error", { error: err?.message || String(err) });
        }
    };
    useEffect(() => {
        if (!socketRef.current) return;
        const handleError = (e) => {
            if (e?.status === "5" || e?.status === 5) {
                setMessages(prev => {
                    const lastIdx = [...prev].reverse().findIndex(m => m.uploading === true && m.isUser === true && m.type !== 'text');
                    if (lastIdx === -1) {
                        return prev;
                    }
                    const idxToUpdate = prev.length - 1 - lastIdx;
                    const msg = prev[idxToUpdate];
                    const errorMsg = e?.message || "Payment required to send files. Free users can only send text messages.";
                    if (shouldLogMediaUpload(msg.type, { name: msg.fileName, type: msg.mime, size: msg._rawFileSize })) {
                        logMediaUpload("1-on-1", "socket-error-status-5", {
                            name: msg.fileName,
                            type: msg.mime,
                            size: msg._rawFileSize,
                        }, {
                            fileType: msg.type,
                            socketError: e,
                            tempId: msg.tempId,
                        });
                    }
                    return prev.map((m, i) =>
                        i === idxToUpdate
                            ? { ...m, uploading: false, uploadFailed: true, uploadError: errorMsg }
                            : m
                    );
                });
                toast.dismiss();
                toast.error(e?.message || "Payment required to send files. Free users can only send text messages.");
            }
        };
        socketRef.current.on("error", handleError);
        return () => {
            socketRef.current.off("error", handleError);
        };
    }, [socketRef.current]);
    const handleMicButton = () => {
        setShowVoice(true);
        setShowAttachModal(false);
        setShowVoiceAttachModal(false);
    };

    const handleReturnToInput = () => {
        setShowVoice(false);
        setShowVoiceAttachModal(false);
    };

    useEffect(() => {
        }, [showVoiceSendModal]);

    useEffect(() => {
        if (window.innerWidth >= 768) setShowSidebar(true);
    }, []);

    // ESC key handler to deselect contact and return to chatbar
    useEffect(() => {
        const handleEscKey = (e) => {
            if (e.key === "Escape" && selectedContactId) {
                setSelectedContactId(null);
                setMessages([]);
                setShowSidebar(true);
            }
        };

        window.addEventListener("keydown", handleEscKey);
        return () => window.removeEventListener("keydown", handleEscKey);
    }, [selectedContactId]);

    return (
        <div className="h-full min-h-0 overflow-hidden bg-[#F3F0FF]">
            {/* Hidden file input used by AttachmentModal picks */}
            <input
                ref={fileInputRef}
                type="file"
                style={{ display: "none" }}
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                        handleFileInput(file);
                    }
                    // Reset input so same file can be selected again
                    e.target.value = "";
                }}
            />
            <div className="h-full min-h-0 rounded-2xl shadow-lg overflow-hidden">
                <div className="flex h-full min-h-0">
                    {/* ChatBar - Show on desktop always, on mobile only when sidebar is shown */}
                    <div className={`${showSidebar ? "block" : "hidden"} md:block w-full md:w-[360px] md:min-w-[300px] md:flex-shrink-0 h-full min-h-0`}>
                        <ChatBar
                            contacts={contacts}
                            groupList={[]}
                            bdayData={birthday}
                            friendResData={friendRes}
                            onSelectContact={handleContactSelect}
                            onSelectGroupDetails={handleSelectGroupDetails}
                            onGroupInfoClick={handleGroupInfoClick}
                            onGroupUnmoved={handleGroupUnmoved}
                            chatScreen={true}
                            selectedContactId={selectedContactId}
                            selectedGroupId={selectedGroupId}
                            filterSaw={true}
                            loading={loading}
                            chatListLoaded={chatListLoaded}
                        />
                    </div>

                    {/* Messages Area - Show on desktop always, on mobile only when contact is selected */}
                    <div
                        className={`${showSidebar ? "hidden" : "block"} md:block relative w-full md:flex-1 h-full min-h-0 overflow-hidden`}
                    >
                        {/* 🔹 NEW: Show GroupDetails when info clicked, GroupChatScreen for group chat, ChatMessagesArea for individual chats */}
                        {/* Determine a groupData object from contacts or fetched group list (computed below when needed) */}

                        {selectedGroupId && showGroupDetails ? (
                            <GroupDetails
                                groupData={contacts.find((g) => String(g.group_id) === String(selectedGroupId)) || groupList.find((g) => String(g.group_id) === String(selectedGroupId))}
                                fetchGroups={fetchGroups}
                                refreshGroupDetails={async (id) => await fetchGroups()}
                                handleGroupDetails={() => setShowGroupDetails(false)}
                                onBack={() => {
                                    setShowGroupDetails(false);
                                    if (window.innerWidth < 768) setShowSidebar(true);
                                }}
                            />
                        ) : selectedGroupId ? (
                            <GroupChatScreen
                                groupId={selectedGroupId}
                                onBackMobile={() => setShowSidebar(true)}
                                groupList={groupList.length ? groupList : contacts.filter((c) => c?.group_id || c?.is_group === 1)}
                                groupData={contacts.find((g) => String(g.group_id) === String(selectedGroupId)) || groupList.find((g) => String(g.group_id) === String(selectedGroupId))}
                                onMarkAsRead={() => {}}
                                onMessageReceived={handleGroupMessageReceived}
                                onHeaderClick={() => setShowGroupDetails(true)}
                                allContacts={contacts.filter((c) => c?.user_id && !c?.group_id && c?.is_group !== 1 && c?.is_group !== "1")}
                                onShareMessage={handleShareMessage}
                            />
                        ) : (
                            <>
                                <FriendVoiceSendModal
                                    open={showVoiceSendModal}
                                    userId={selectedContactId}
                                    onClose={() => {
                                        setShowVoiceSendModal(false);
                                        setShowVoice(false);
                                    }}
                                />

                                <ChatMessagesArea
                                    messages={messages}
                                    contact={
                                        contacts.find((c) => c.user_id === selectedContactId) ||
                                        contactDetails.find((c) => c.user_id === selectedContactId)
                                    }
                                    status={status}
                                    onBackMobile={() => setShowSidebar(true)}
                                    showVoice={showVoice}
                                    voiceTime={voiceTime}
                                    onMicButton={handleMicButton}
                                    onSendVoiceClick={() => {
                                        setShowVoiceSendModal(true);
                                    }}
                                    onReturnToInput={handleReturnToInput}
                                    showAttachModal={showAttachModal}
                                    setShowAttachModal={setShowAttachModal}
                                    handleAttachPick={handleAttachPick}
                                    showVoiceAttachModal={showVoiceAttachModal}
                                    setShowVoiceAttachModal={setShowVoiceAttachModal}
                                    message={message}
                                    setMessage={setMessage}
                                    onSendMessage={handleSendMessage}
                                    uploadProgress={uploadProgress}
                                    isUploading={isUploading}
                                    fileInputRef={fileInputRef}
                                    selectedContactId={selectedContactId}
                                    onFileSelect={handleFileSelect}
                                    showPermissionModal={showPermissionModal}
                                    setShowPermissionModal={setShowPermissionModal}
                                    markContactRead={markContactRead}
                                    allContacts={contacts}
                                    onShareMessage={handleShareMessage}
                                    currentUserId={profile?.user_id}
                                    onViewOnceImage={handleViewOnceImage}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

